// conversationReducer.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchConversations,
  fetchChatSessions,
  fetchAnonymizedChatSessions,
  fetchTranscripts,
  fetchComments,
  addComment,
  editComment,
  deleteComment,
  setConversationShadowBanned,
  fetchStructuredLogs,
  generateChatSessionAudio,
  fetchMemoryData,
  fetchCortexState,
  triggerMemoryForConversation,
  fetchConversationIds,
  fetchSingleConversation,
  translateTexts,
  isoNow,
} from "../actions/conversationsActions";
import { UserName } from "./userReducer";
import {
  ChatAnnotation,
  ChatMessageModality,
} from "../../components/dashboard/conversations/message-bubble/MessageBubbleTypes";
import { getTargetId } from "../../utils/render";

export interface Conversation {
  id: string;
  human: any;
  bot: any;
  config?: any;
  sms_active?: boolean;
  route_to_debug?: boolean;
  introduced_by?: any;
  queue_offset?: number;
  sent_cursor?: any;
  created_at: string;
  version_major?: number;
  version_minor?: number;
  owning_user_id?: string;
  is_private?: boolean;
  environment?: string;
  latest_chat_message_time?: string;
  shadow_banned?: boolean;
}

export interface Transcript {
  conversation_id: string;
  created_at: string;
  environment: string;
  human_initial: string;
  bot_name: string;
  latest_chat_message_time?: string;
  num_turns_in_latest_session: number;
  is_private?: boolean;
}

interface SessionAudioMap {
  [chatSessionId: string]: {
    audioUrl?: string;
    loading?: boolean;
    error?: string;
  };
}

export enum LogSchema {
  EVENTSPROD = "eventsprod",
  DEV_EVENTSPROD = "dev_eventsprod",
}

export interface ChatSession {
  id: string;
  start_time: string;
  end_time: string;
  messages: any[];
  launch_args_id?: string;
  agent_config?: any;
  behavior_config?: any;
  organization_id?: string;
  metadata?: {
    assessment?: {
      assessment_id: string;
      processed_version?: number;
    };
    roleplay?: {
      roleplay_id: string;
      processed_version?: number;
    };
    scene_type?: string;
    primary_language?: string;
    originating_pixel_streaming_session_id?: string;
  };
}

export interface Message {
  responding_to?: string;
  triggered_by_action_id?: string;
  client_msg_uid: string;
  id: number;
  chat_message_id: number;
  chat_session_id: string;
  created_at: string;
  type: "human" | "bot";
  content: string;
  annotations?: ChatAnnotation[];
  media_ref?: {
    blob_path: string;
    content_type: string;
  };
  modality?: ChatMessageModality;
  content_type?: string;
  stored_audio?: {
    path: string;
    container: string; // e.g., "audio-vad"
  };
  has_stored_audio?: boolean;
  percent_spoken?: number;
  chat_speaker_id: string;
  voice_args?: {
    personality?: string;
    voice?: string;
    emotion?: string;
  } | null;
  markup?: string | null;
  redacted_content?: string | null;
}

export interface AnnotationUser {
  id: string;
  name: UserName;
}

export interface Comment {
  id: string;
  conversation_id: string;
  target_id?: string;
  user: AnnotationUser;
  comment: string;
  thread_id: string;
  created_at: string;
  updated_at?: string;
  deleted_at?: string;
  tags?: string[];
}

interface ConversationState {
  conversations: Conversation[];
  transcripts: Transcript[];
  anonymizedChatSessions: ChatSession[];
  chatSessions: ChatSession[];
  comments: Comment[];
  loading: boolean;
  loadingAllTranscripts: boolean;
  loadingTranscript: boolean;
  loadingConversation: boolean;
  loadingComments: boolean;
  error: string | null;
  structuredLogs: { [key: string]: any };
  loadingStructuredLogs: { [key: string]: boolean };
  lastFetchTime: number;
  lastTranscriptFetchTime: number;
  sessionAudio: SessionAudioMap;
  memoryData: { [key: string]: any };
  loadingMemoryData: { [key: string]: boolean };
  loadingMemoryTrigger: { [key: string]: boolean };
  cortexData: { [key: string]: any };
  loadingCortexData: { [key: string]: boolean };
  structuredLogsDev: { [key: string]: any };
  loadingStructuredLogsDev: { [key: string]: boolean };
  conversationIds: string[];
  lastConversationIdsFetchTime: number;
  page_number: number;
  cursor: string | null;
  loadingMore: boolean;
  hasMoreEntries: boolean;
  translations: { [key: string]: string };
  translating: boolean;
  showTranslationForMessage: { [messageId: string]: boolean };
  showRedactedForMessage: { [messageId: string]: boolean };
}

const initialState: ConversationState = {
  conversations: [],
  transcripts: [],
  chatSessions: [],
  anonymizedChatSessions: [],
  comments: [],
  loading: false,
  loadingAllTranscripts: false,
  loadingTranscript: false,
  loadingConversation: false,
  loadingComments: false,
  error: null,
  structuredLogs: {},
  loadingStructuredLogs: {},
  // this is annoying but cleaner to have separate state for dev structured logs
  structuredLogsDev: {},
  loadingStructuredLogsDev: {},
  lastFetchTime: 0,
  lastTranscriptFetchTime: 0,
  sessionAudio: {},
  memoryData: {},
  loadingMemoryData: {},
  loadingMemoryTrigger: {},
  cortexData: {},
  loadingCortexData: {},
  page_number: 0,
  cursor: null,
  loadingMore: false,
  hasMoreEntries: false,
  conversationIds: [],
  lastConversationIdsFetchTime: 0,
  translations: {},
  translating: false,
  showTranslationForMessage: {},
  showRedactedForMessage: {},
};

const conversationSlice = createSlice({
  name: "conversations",
  initialState,
  reducers: {
    conversationAdded: (state, action: PayloadAction<Conversation>) => {
      state.conversations.push(action.payload);
    },
    toggleMessageTranslation: (state, action: PayloadAction<string>) => {
      const messageId = action.payload;
      state.showTranslationForMessage[messageId] = !state.showTranslationForMessage[messageId];
    },
    setAllMessagesTranslation: (state, action: PayloadAction<{ messageIds: string[]; show: boolean }>) => {
      action.payload.messageIds.forEach(id => {
        state.showTranslationForMessage[id] = action.payload.show;
      });
    },
    toggleMessageRedaction: (state, action: PayloadAction<string>) => {
      const messageId = action.payload;
      // Initialize if undefined (handles persisted state from before this feature)
      if (!state.showRedactedForMessage) {
        state.showRedactedForMessage = {};
      }
      // Default is true (show redacted), toggle switches it
      const currentValue = state.showRedactedForMessage[messageId] ?? true;
      state.showRedactedForMessage[messageId] = !currentValue;
    },
    setAllMessagesRedaction: (state, action: PayloadAction<{ messageIds: string[]; showRedacted: boolean }>) => {
      // Initialize if undefined (handles persisted state from before this feature)
      if (!state.showRedactedForMessage) {
        state.showRedactedForMessage = {};
      }
      action.payload.messageIds.forEach(id => {
        state.showRedactedForMessage[id] = action.payload.showRedacted;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // structured log modal actions
      .addCase(fetchStructuredLogs.pending, (state, action) => {
        const { startMsgId, chatSessionId } = action.meta.arg;
        const targetId = getTargetId(startMsgId, chatSessionId);
        const usedSchema = action.meta.arg.targetSchema ?? LogSchema.EVENTSPROD;
        if (usedSchema === LogSchema.DEV_EVENTSPROD) {
          state.loadingStructuredLogsDev[targetId] = true;
        } else {
          state.loadingStructuredLogs[targetId] = true;
        }
        state.error = null;
      })
      .addCase(fetchStructuredLogs.fulfilled, (state, action) => {
        const { data, targetId, usedSchema } = action.payload;
        if (usedSchema === LogSchema.DEV_EVENTSPROD) {
          state.loadingStructuredLogsDev[targetId] = false;
          state.structuredLogsDev[targetId] = data;
        } else {
          state.loadingStructuredLogs[targetId] = false;
          state.structuredLogs[targetId] = data;
        }
      })
      .addCase(fetchStructuredLogs.rejected, (state, action) => {
        const { startMsgId, chatSessionId } = action.meta.arg;
        const targetId = getTargetId(startMsgId, chatSessionId);
        const usedSchema = action.meta.arg.targetSchema ?? "eventsprod";
        if (usedSchema === LogSchema.DEV_EVENTSPROD) {
          state.loadingStructuredLogsDev[targetId] = false;
        } else {
          state.loadingStructuredLogs[targetId] = false;
        }
        state.error =
          action.error.message || "Failed to fetch structured logs.";
      })
      // Handle fetchConversations action
      .addCase(fetchConversations.pending, (state, action) => {
        if (
          action.meta.arg.page_number === undefined ||
          action.meta.arg.page_number === 0
        ) {
          state.loading = true;
          state.cursor = isoNow();
        } else {
          state.loadingMore = true;
        }
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingMore = false;

        const isPagination = (action.meta.arg.page_number ?? 0) > 0;

        if (isPagination) {
          const newcomers = action.payload.data.filter(
            (c: { id: string }) =>
              !state.conversations.some((p) => p.id === c.id)
          );
          state.conversations.push(...newcomers);
        } else {
          state.conversations = action.payload.data;
        }
        state.page_number = action.payload.page_number ?? 0;
        state.hasMoreEntries = action.payload.has_more_entries;

        if (!action.payload.fromCache) {
          state.lastFetchTime = Date.now();
        }
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch conversations.";
      })
      // Handle fetchChatSessions action
      .addCase(fetchChatSessions.pending, (state) => {
        state.loadingConversation = true;
        state.error = null;
        state.chatSessions = [];
      })
      .addCase(
        fetchChatSessions.fulfilled,
        (state, action: PayloadAction<ChatSession[]>) => {
          state.loadingConversation = false;
          state.chatSessions = action.payload;
        }
      )
      .addCase(fetchChatSessions.rejected, (state, action) => {
        state.loadingConversation = false;
        state.error = action.error.message || "Failed to fetch chat sessions.";
      })
      // Handle fetchAnonymizedChatSessions action
      .addCase(fetchAnonymizedChatSessions.pending, (state) => {
        state.loadingTranscript = true;
        state.error = null;
        state.anonymizedChatSessions = [];
      })
      .addCase(
        fetchAnonymizedChatSessions.fulfilled,
        (state, action: PayloadAction<ChatSession[]>) => {
          state.loadingTranscript = false;
          state.anonymizedChatSessions = action.payload;
        }
      )
      .addCase(fetchAnonymizedChatSessions.rejected, (state, action) => {
        state.loadingTranscript = false;
        state.error =
          action.error.message || "Failed to fetch anonymized chat sessions.";
      })
      // Handle fetchTranscripts action
      .addCase(fetchTranscripts.pending, (state) => {
        state.loadingAllTranscripts = true;
        state.error = null;
      })
      .addCase(fetchTranscripts.fulfilled, (state, action) => {
        state.loadingAllTranscripts = false;
        state.transcripts = action.payload.data;
        if (!action.payload.fromCache) {
          state.lastTranscriptFetchTime = Date.now();
        }
      })
      .addCase(fetchTranscripts.rejected, (state, action) => {
        state.loadingAllTranscripts = false;
        state.error = action.error.message || "Failed to fetch transcripts.";
      })
      // Handle fetchComments action
      .addCase(fetchComments.pending, (state) => {
        state.loadingComments = true;
        state.error = null;
        state.comments = [];
      })
      .addCase(
        fetchComments.fulfilled,
        (state, action: PayloadAction<Comment[]>) => {
          state.loadingComments = false;
          state.comments = action.payload;
        }
      )
      .addCase(fetchComments.rejected, (state, action) => {
        state.loadingComments = false;
        state.error = action.error.message || "Failed to fetch comments.";
      })
      // Handle addComment action
      .addCase(
        addComment.fulfilled,
        (state, action: PayloadAction<Comment>) => {
          state.comments.push(action.payload);
        }
      )
      // Handle editComment action
      .addCase(
        editComment.fulfilled,
        (state, action: PayloadAction<Comment>) => {
          const index = state.comments.findIndex(
            (comment) => comment.id === action.payload.id
          );
          if (index !== -1) {
            state.comments[index] = action.payload;
          }
        }
      )
      .addCase(generateChatSessionAudio.pending, (state, action) => {
        const { chatSessionId } = action.meta.arg;
        state.sessionAudio[chatSessionId] = {
          loading: true,
          error: undefined,
        };
      })
      .addCase(generateChatSessionAudio.fulfilled, (state, action) => {
        const { chatSessionId, audioUrl } = action.payload;
        state.sessionAudio[chatSessionId] = {
          loading: false,
          audioUrl: audioUrl,
        };
      })
      .addCase(generateChatSessionAudio.rejected, (state, action) => {
        const { chatSessionId } = action.meta.arg;
        state.sessionAudio[chatSessionId] = {
          loading: false,
          error: action.error.message || "Failed to generate audio",
        };
      })

      // Handle fetchMemoryData action

      .addCase(fetchMemoryData.pending, (state, action) => {
        const conversationId = action.meta.arg;
        state.loadingMemoryData[conversationId] = true;
        state.error = null;
      })

      .addCase(fetchMemoryData.fulfilled, (state, action) => {
        const conversationId = action.meta.arg;
        state.loadingMemoryData[conversationId] = false;
        state.memoryData[conversationId] = action.payload;
      })

      .addCase(fetchMemoryData.rejected, (state, action) => {
        const conversationId = action.meta.arg;
        state.loadingMemoryData[conversationId] = false;
        state.error = action.error.message || "Failed to fetch memory data.";
      })

      // Handle fetchCortexState action

      .addCase(fetchCortexState.pending, (state, action) => {
        const { chatSessionId, conversationId } = action.meta.arg;
        const key = `${conversationId}-${chatSessionId}`;
        state.loadingCortexData[key] = true;
        state.error = null;
      })

      .addCase(fetchCortexState.fulfilled, (state, action) => {
        const { chatSessionId, conversationId } = action.meta.arg;
        const key = `${conversationId}-${chatSessionId}`;
        state.loadingCortexData[key] = false;
        state.cortexData[key] = action.payload;
      })

      .addCase(fetchCortexState.rejected, (state, action) => {
        const { chatSessionId, conversationId } = action.meta.arg;
        const key = `${conversationId}-${chatSessionId}`;
        state.loadingCortexData[key] = false;
        state.error =
          action.error.message ||
          "Unpickling error - failed to fetch cortex state.";
      })
      // Handle deleteComment action
      .addCase(
        deleteComment.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.comments = state.comments.filter(
            (comment) => comment.id !== action.payload
          );
        }
      )
      // ------------------------------------------------------
      // Handle triggerMemoryForConversation
      // ------------------------------------------------------
      .addCase(triggerMemoryForConversation.pending, (state, action) => {
        const conversationId = action.meta.arg.conversationId;
        state.loadingMemoryTrigger[conversationId] = true;
      })
      .addCase(triggerMemoryForConversation.fulfilled, (state, action) => {
        const conversationId = action.meta.arg.conversationId;
        state.loadingMemoryTrigger[conversationId] = false;
      })
      .addCase(triggerMemoryForConversation.rejected, (state, action) => {
        const conversationId = action.meta.arg.conversationId;
        state.loadingMemoryTrigger[conversationId] = false;
        state.error = action.error.message || "Failed to trigger memory.";
      })
      // Handle setConversationShadowBanned action
      .addCase(
        setConversationShadowBanned.fulfilled,
        (
          state,
          action: PayloadAction<{
            conversationId: string;
            newValue: boolean;
          }>
        ) => {
          const index = state.conversations.findIndex(
            (convo) => convo.id === action.payload.conversationId
          );
          if (index !== -1) {
            state.conversations[index].shadow_banned = action.payload.newValue;
          }
        }
      )
      // Handle fetchConversationIds action
      .addCase(fetchConversationIds.pending, (state) => {
        // No need to set a loading flag as this is used in the background
        state.error = null;
      })
      .addCase(fetchConversationIds.fulfilled, (state, action) => {
        state.conversationIds = action.payload.data;
        if (!action.payload.fromCache) {
          state.lastConversationIdsFetchTime = Date.now();
        }
      })
      .addCase(fetchConversationIds.rejected, (state, action) => {
        state.error =
          action.error.message || "Failed to fetch conversation IDs.";
      })
      // Handle fetchSingleConversation action
      .addCase(fetchSingleConversation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSingleConversation.fulfilled, (state, action) => {
        state.loading = false;

        // Check if the conversation already exists in the state
        const existingIndex = state.conversations.findIndex(
          (c) => c.id === action.payload.id
        );

        if (existingIndex !== -1) {
          // Update existing conversation
          state.conversations[existingIndex] = action.payload;
        } else {
          // Add to conversations array
          state.conversations.push(action.payload);
        }
      })
      .addCase(fetchSingleConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch conversation.";
      })
      // Handle translateTexts action
      .addCase(translateTexts.pending, (state) => {
        state.translating = true;
        state.error = null;
      })
      .addCase(translateTexts.fulfilled, (state, action) => {
        state.translating = false;
        const { texts, translations } = action.payload;
        // Store translations keyed by original text
        texts.forEach((text, index) => {
          state.translations[text] = translations[index];
        });
      })
      .addCase(translateTexts.rejected, (state, action) => {
        state.translating = false;
        state.error = action.error.message || "Failed to translate texts.";
      });
  },
});

export const { conversationAdded, toggleMessageTranslation, setAllMessagesTranslation, toggleMessageRedaction, setAllMessagesRedaction } = conversationSlice.actions;
export default conversationSlice.reducer;
